import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, IconButton, InputAdornment, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import { Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { EmailSettingsStatus } from '../../../../sic-lib/enums/EmailSettingsStatus';
import { splitCamelCase } from '../../../../sic-lib/utils/aiHelpers';
import DialogUserEmailInstructions from '../DialogUserEmailInstructions/DialogUserEmailInstructions';

interface Provider {
    id: number;
    name: string;
    imapHost: string;
    smtpHost: string;
}

interface Props {
    email: string;
    password: string;
    status: number;
    isShowAdvance: React.Dispatch<boolean>;
    onSettingsChange: React.Dispatch<{
        imapHost?: string;
        smtpHost?: string;
        emailUserName?: string;
        emailUserPassword?: string;
    }>;
}

export default function SelectUserEmail({
    email,
    password,
    status,
    isShowAdvance,
    onSettingsChange,
}: Props) {
    const { classes, cx } = useStyles();

    const [provider, setProvider] = React.useState<Provider>(Providers[0]);
    const [openInstructionDialog, setOpenInstructionDialog] = React.useState<boolean>(false);

    const handleSelect = (provider: Provider) => {
        setProvider(provider);
        if (provider.id === 0) {
            isShowAdvance(true);
        } else {
            onSettingsChange({
                imapHost: provider.imapHost,
                smtpHost: provider.smtpHost,
            });
            isShowAdvance(false);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        onSettingsChange({ [name]: value });
    };

    const handleOpenInstructionDialog = () => {
        setOpenInstructionDialog(!openInstructionDialog);
    };

    return (
        <>
            <Grid container item direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={12} sm={4}>
                    <Typography component="p" variant="body2">
                        Select Email Provider
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <TextField
                        select
                        value={provider.name}
                        id="select_provider"
                        label="Select Email Provider"
                        fullWidth
                        SelectProps={{
                            startAdornment: (provider.id === 1 || provider.id === 3) && (
                                <InputAdornment position="start">
                                    <Tooltip title="Setup Instructions" placement="top">
                                        <IconButton
                                            size="small"
                                            onClick={handleOpenInstructionDialog}
                                        >
                                            <InfoOutlinedIcon />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    >
                        {Providers.map(prov => (
                            <MenuItem
                                key={prov.id}
                                value={prov.name}
                                onClick={() => handleSelect(prov)}
                            >
                                {prov.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>
            <Grid container item direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={12} sm={4}>
                    <Typography component="p" variant="body2">
                        Email Address (account user name):
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <TextField
                        name="emailUserName"
                        label="Email"
                        value={email}
                        type="email"
                        onChange={handleChange}
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Grid container item direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={12} sm={4}>
                    <Typography component="p" variant="body2">
                        Password:
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <TextField
                        name="emailUserPassword"
                        label="Password"
                        value={password}
                        type="password"
                        onChange={handleChange}
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Grid container item direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={12} sm={4}>
                    <Typography component="p" variant="body2">
                        Status:
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <Alert severity={status === 1 || status === 5 ? 'success' : 'error'}>
                        {splitCamelCase(
                            Object.keys(EmailSettingsStatus).find(
                                (key: string) =>
                                    EmailSettingsStatus[key as keyof typeof EmailSettingsStatus] ===
                                    status,
                            ) || '',
                        )}
                    </Alert>
                </Grid>
            </Grid>
            <DialogUserEmailInstructions
                emailProvider={provider.id}
                isOpen={openInstructionDialog}
                onCloseHandler={handleOpenInstructionDialog}
            />
        </>
    );
}

const Providers = [
    // {
    //     id: 0,
    //     name: 'Select Email Provider',
    //     imapHost: '',
    //     smtpHost: '',
    // },
    {
        id: 0,
        name: 'Custom',
        imapHost: '',
        smtpHost: '',
    },
    {
        id: 1,
        name: 'GMail/Google Workspace',
        imapHost: 'imap.gmail.com',
        smtpHost: 'smtp.gmail.com',
    },
    {
        id: 2,
        name: 'SuccessWebsite',
        imapHost: 'imap.everyone.net',
        smtpHost: 'smtp.everyone.net',
    },
    {
        id: 3,
        name: 'Outlook.com/Office 365',
        imapHost: 'outlook.office365.com',
        smtpHost: 'smtp.office365.com',
    },
    /*
    {
        id: 4,
            name: 'Yahoo',
                imapHost: 'imap.mail.yahoo.com',
                    smtpHost: 'smtp.mail.yahoo.com'
    },
    {
        id: 5,
            name: 'Zoho',
                imapHost: 'imap.zoho.com',
                    smtpHost: 'smtp.zoho.com'
    },
    */
];

const useStyles = makeStyles()((theme: Theme) => ({
    emailProvider: {
        position: 'relative',
        padding: theme.spacing(2),
        '& > h3': {
            textTransform: 'capitalize',
        },
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
    },
    menuItem: {
        '&:hover': {
            backgroundColor: theme.palette.menuListHover.light,
        },
        '&.Mui-selected': {
            backgroundColor: theme.palette.menuListHover.main,
            '&:hover': {
                backgroundColor: theme.palette.menuListHover.main,
            },
        },
    },
    status: {
        backgroundColor: theme.palette.info.light,
        padding: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
    },
    statusSucess: {
        backgroundColor: theme.palette.conversationTimelinePaper.light,
    },
    statusError: {
        backgroundColor: theme.palette.error.light,
    },
}));
